import type { ReservationValues } from './types'

const STORAGE_KEY = 'flightstay-reservation-random-ids-v1'

const ID_RULES = {
  bookingId: { prefix: '7041', minimum: '7041465836' },
  bookingReference: { prefix: '5489', minimum: '5489524365' },
  memberId: { prefix: '4495', minimum: '4495415814' },
} as const

type IdKey = keyof typeof ID_RULES
type UsedIds = Partial<Record<IdKey, string[]>>

function loadUsedIds(): UsedIds {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null')
    if (saved && typeof saved === 'object') return saved as UsedIds
  } catch {
    // Recover with an empty history if browser storage is unavailable or invalid.
  }
  return {}
}

function randomSixDigitSuffix(minimumSuffix: number, used: Set<string>): string {
  const firstAllowed = minimumSuffix + 1
  const availableCount = 1_000_000 - firstAllowed
  if (availableCount <= 0) throw new Error('No valid reservation ID remains above the configured minimum.')

  const random = new Uint32Array(1)
  for (let attempt = 0; attempt < 100; attempt += 1) {
    let randomNumber: number
    if (globalThis.crypto?.getRandomValues) {
      globalThis.crypto.getRandomValues(random)
      randomNumber = (random[0] ?? 0) % availableCount
    } else {
      randomNumber = Math.floor(Math.random() * availableCount)
    }
    const suffix = String(firstAllowed + randomNumber).padStart(6, '0')
    if (!used.has(suffix)) return suffix
  }

  // Extremely unlikely fallback: find the first unused valid suffix.
  for (let suffixNumber = firstAllowed; suffixNumber < 1_000_000; suffixNumber += 1) {
    const suffix = String(suffixNumber).padStart(6, '0')
    if (!used.has(suffix)) return suffix
  }
  throw new Error('Could not generate a unique reservation ID.')
}

function nextId(key: IdKey, usedIds: UsedIds): string {
  const rule = ID_RULES[key]
  const used = new Set(usedIds[key] ?? [])
  const minimumSuffix = Number(rule.minimum.slice(rule.prefix.length))
  const suffix = randomSixDigitSuffix(minimumSuffix, used)
  const id = `${rule.prefix}${suffix}`
  usedIds[key] = [...used, suffix].slice(-1000)
  return id
}

export function nextReservationIds(): Pick<ReservationValues, 'bookingId' | 'bookingReference' | 'memberId'> {
  const usedIds = loadUsedIds()
  const ids = {
    bookingId: nextId('bookingId', usedIds),
    bookingReference: nextId('bookingReference', usedIds),
    memberId: nextId('memberId', usedIds),
  }
  // Storage can be disabled or full (for example, in private browsing). IDs
  // can still be generated for this reservation when persistence is unavailable.
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(usedIds))
  } catch {
    // A fresh random suffix is still generated on the next request.
  }
  return ids
}
